import { Input } from '@rocco/components/input';
import { Label } from '@rocco/components/label';
import { cn } from '@rocco/utils/cn';

/**
 * Mobile confirmation screen for booking a meeting.
 * Displays the selected meeting details and collects user information (name, email, company).
 * Includes form validation and handles the final booking confirmation step.
 */
interface BookingConfirmationMobileProps {
    consultantName: string;
    formattedDate: string;
    formattedTime: string;
    timezoneAbbr: string;
    userName: string;
    userEmail: string;
    userCompany: string;
    onUserNameChange: (value: string) => void;
    onUserEmailChange: (value: string) => void;
    onUserCompanyChange: (value: string) => void;
    onBack: () => void;
    onConfirm: () => void;
    isConfirmDisabled: boolean;
    isLoading: boolean;
    confirmButtonLabel: string;
    userEmailLabel: string;
    /** When false the details inputs are hidden and the stored booking details are reused */
    showDetailsForm?: boolean;
    showValidationErrors: boolean;
    nameErrorMessage?: string;
    emailErrorMessage?: string;
    companyErrorMessage?: string;
    areInputsDisabled?: boolean;
    isCompanyInputDisabled?: boolean;
}

export const BookingConfirmationMobile = ({
    consultantName,
    formattedDate,
    formattedTime,
    timezoneAbbr,
    userName,
    userEmail,
    userCompany,
    onUserNameChange,
    onUserEmailChange,
    onUserCompanyChange,
    onBack,
    onConfirm,
    isConfirmDisabled,
    isLoading,
    confirmButtonLabel,
    userEmailLabel,
    showDetailsForm = true,
    showValidationErrors,
    nameErrorMessage,
    emailErrorMessage,
    companyErrorMessage,
    areInputsDisabled = false,
    isCompanyInputDisabled = false,
}: BookingConfirmationMobileProps) => {
    const shouldShowNameError = showValidationErrors && nameErrorMessage;
    const shouldShowEmailError = showValidationErrors && emailErrorMessage;
    const shouldShowCompanyError = showValidationErrors && companyErrorMessage;

    return (
        <div className="mx-auto w-full bg-white text-[#161717]">
            <header className="bg-brick-600 px-6 py-5 text-white">
                <h1 className="text-6 leading-6">Confirm booking</h1>
            </header>

            <div className="space-y-4 px-4 py-6">
                <section className="rounded-[14px] border px-4 py-5">
                    <p className="text-center text-sm text-[#4B4B4B]">
                        You're requesting a meeting with {consultantName} for:
                    </p>
                    <div className="mt-3 text-center">
                        <p className="text-lg font-semibold text-[#161717]">
                            {formattedDate}
                        </p>
                        <p className="mt-1 text-lg font-semibold text-[#161717]">
                            {formattedTime} {timezoneAbbr}
                        </p>
                    </div>
                </section>

                {!showDetailsForm ? (
                    <p className="text-sm text-[#4B4B4B]">
                        We'll keep the details from your original booking.
                    </p>
                ) : (
                    <section className="space-y-4">
                        <div>
                            <Label
                                htmlFor="booking-user-name-mobile"
                                className="text-sm font-semibold text-[#161717]"
                            >
                                Your Name*
                            </Label>
                            <Input
                                id="booking-user-name-mobile"
                                maxLength={50}
                                value={userName}
                                onChange={(event) =>
                                    onUserNameChange(event.target.value)
                                }
                                placeholder="Enter your full name"
                                disabled={areInputsDisabled}
                                className={cn(
                                    'mt-2',
                                    areInputsDisabled &&
                                        'overflow-hidden rounded-2 bg-gray-100',
                                )}
                                autoComplete="name"
                                color="lightOutline"
                                required
                                aria-invalid={
                                    shouldShowNameError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowNameError
                                        ? 'booking-user-name-mobile-error'
                                        : undefined
                                }
                            />
                            {shouldShowNameError ? (
                                <p
                                    id="booking-user-name-mobile-error"
                                    className="mt-2 text-sm text-[red]"
                                >
                                    {nameErrorMessage}
                                </p>
                            ) : null}
                        </div>

                        <div>
                            <Label
                                htmlFor="booking-user-email-mobile"
                                className="text-sm font-semibold text-[#161717]"
                            >
                                Your Email*
                            </Label>
                            <Input
                                id="booking-user-email-mobile"
                                type="email"
                                value={userEmail}
                                onChange={(event) =>
                                    onUserEmailChange(event.target.value)
                                }
                                placeholder={userEmailLabel}
                                disabled={areInputsDisabled}
                                className={cn(
                                    'mt-2',
                                    areInputsDisabled &&
                                        'overflow-hidden rounded-2 bg-gray-100',
                                )}
                                autoComplete="email"
                                color="lightOutline"
                                required
                                aria-invalid={
                                    shouldShowEmailError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowEmailError
                                        ? 'booking-user-email-mobile-error'
                                        : undefined
                                }
                            />
                            {shouldShowEmailError ? (
                                <p
                                    id="booking-user-email-mobile-error"
                                    className="mt-2 text-sm text-[red]"
                                >
                                    {emailErrorMessage}
                                </p>
                            ) : null}
                        </div>

                        <div>
                            <Label
                                htmlFor="booking-user-company-mobile"
                                className="text-sm font-semibold text-[#161717]"
                            >
                                Your Company
                            </Label>
                            <Input
                                id="booking-user-company-mobile"
                                maxLength={50}
                                value={userCompany}
                                onChange={(event) =>
                                    onUserCompanyChange(event.target.value)
                                }
                                placeholder="Company name (optional)"
                                disabled={isCompanyInputDisabled}
                                className={cn(
                                    'mt-2',
                                    isCompanyInputDisabled &&
                                        'overflow-hidden rounded-2 bg-gray-100',
                                )}
                                autoComplete="organization"
                                color="lightOutline"
                                aria-invalid={
                                    shouldShowCompanyError ? true : undefined
                                }
                                aria-describedby={
                                    shouldShowCompanyError
                                        ? 'booking-user-company-mobile-error'
                                        : undefined
                                }
                            />
                            {shouldShowCompanyError ? (
                                <p
                                    id="booking-user-company-mobile-error"
                                    className="mt-2 text-sm text-[red]"
                                >
                                    {companyErrorMessage}
                                </p>
                            ) : null}
                        </div>
                    </section>
                )}
            </div>

            <footer className="flex gap-2 px-4 pb-6">
                <button
                    type="button"
                    onClick={onBack}
                    className="h-12 flex-1 rounded-full border border-black bg-white text-base font-semibold text-black transition-colors hover:bg-[#FFF5F0]"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={onConfirm}
                    disabled={isConfirmDisabled}
                    className="h-12 flex-1 rounded-full bg-brick-600 text-base font-semibold text-white transition-colors hover:bg-[#c56843] disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {isLoading ? 'Confirming' : confirmButtonLabel}
                </button>
            </footer>
        </div>
    );
};
